import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../axiosInstance';

/**
 * Custom hook for handling drug pricing form submission
 * @returns {Object} Hook state and functions
 */
export const useDrugPricingFormSubmission = () => {
  const [state, setState] = useState({
    loading: false,
    error: null,
    success: false,
    data: null,
  });

  /**
   * Clean form data before submission
   * @param {Object} formData - The form data from Formik
   * @returns {Object} Cleaned data for API
   */
  const cleanFormData = useCallback((formData) => {
    const cleanedData = { ...formData };

    // Convert numeric fields from strings to numbers
    const numericFields = ['proposed_price_local', 'cif_price', 'ex_factory_price'];
    numericFields.forEach(field => {
      if (cleanedData[field] && cleanedData[field] !== '') {
        cleanedData[field] = parseFloat(cleanedData[field]);
      } else if (cleanedData[field] === '') {
        delete cleanedData[field]; // Remove empty string fields
      }
    });

    // Filter out empty reference country prices
    if (cleanedData.reference_country_prices) {
      cleanedData.reference_country_prices = cleanedData.reference_country_prices.filter(
        country => country.country && (country.price || country.price === 0)
      ).map(country => ({
        country: country.country,
        price: parseFloat(country.price) || 0,
        currency: country.currency || 'USD'
      }));
    }

    return cleanedData;
  }, []);

  /**
   * Submit the drug pricing form
   * @param {Object} formData - Form data from Formik
   * @returns {Promise<Object|null>} API response data or null if failed
   */
  const submitForm = useCallback(async (formData) => {
    // Reset state
    setState({
      loading: true,
      error: null,
      success: false,
      data: null,
    });

    try {
      // Clean form data before submission
      const apiData = cleanFormData(formData);

      // Make API call
      const response = await axiosInstance.post('/api/drug-pricing-forms', apiData);

      if (response.data) {
        setState({
          loading: false,
          error: null,
          success: true,
          data: response.data.data,
        });

        // Show success message
        toast.success(
          response.data.message || 'Drug pricing form submitted successfully!',
          { position: "top-center" }
        );

        return response.data;
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Failed to submit drug pricing form';

      setState({
        loading: false,
        error: errorMessage,
        success: false,
        data: null,
      });

      // Error toast will be handled by axios interceptor
      // But we can add a specific message for validation errors
      if (error.response?.status === 400) {
        toast.error(
          'Please check your form data and try again',
          { position: "top-center" }
        );
      }

      return null;
    }
  }, [cleanFormData]);

  /**
   * Update an existing drug pricing form
   * @param {number} formId - The form ID to update
   * @param {Object} formData - Form data from Formik
   * @returns {Promise<Object|null>} API response data or null if failed
   */
  const updateForm = useCallback(async (formId, formData) => {
    if (!formId) {
      toast.error('Form ID is required for updates', { position: "top-center" });
      return null;
    }

    // Reset state
    setState({
      loading: true,
      error: null,
      success: false,
      data: null,
    });

    try {
      // Clean form data before submission
      const apiData = cleanFormData(formData);

      // Make API call
      const response = await axiosInstance.put(`/api/drug-pricing-forms/${formId}`, apiData);

      if (response.data) {
        setState({
          loading: false,
          error: null,
          success: true,
          data: response.data.data,
        });

        // Show success message
        toast.success(
          response.data.message || 'Drug pricing form updated successfully!',
          { position: "top-center" }
        );

        return response.data;
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Failed to update drug pricing form';

      setState({
        loading: false,
        error: errorMessage,
        success: false,
        data: null,
      });

      // Handle specific error cases
      if (error.response?.status === 403) {
        toast.error(
          'You can only update your own forms',
          { position: "top-center" }
        );
      } else if (error.response?.status === 404) {
        toast.error(
          'Drug pricing form not found',
          { position: "top-center" }
        );
      }

      return null;
    }
  }, [cleanFormData]);

  /**
   * Reset the hook state
   */
  const resetState = useCallback(() => {
    setState({
      loading: false,
      error: null,
      success: false,
      data: null,
    });
  }, []);

  return {
    // State
    loading: state.loading,
    error: state.error,
    success: state.success,
    data: state.data,
    
    // Functions
    submitForm,
    updateForm,
    resetState,
    
    // Helper function (exposed for testing or advanced usage)
    cleanFormData,
  };
};

export default useDrugPricingFormSubmission;
